/**
 * Mystery reveal: assemble snap → hold → popcorn color pops → name.
 * Magica Atelier chrome for the ceremony end-state.
 */
import React, { useEffect, useState } from 'react';
import HologramViewport, { FRONT_YAW } from './HologramViewport';
import ShareCard from './ShareCard';

const PHASES = {
  intro: 0,
  assemble: 1,
  hold: 2,
  colorize: 3,
  named: 4,
  share: 5,
};

const INTRO_MS = 600;
const ASSEMBLE_MS = 2600;
const HOLD_MS = 380;
const COLOR_AT = INTRO_MS + ASSEMBLE_MS + HOLD_MS;
const COLOR_DUR = 1250;
const NAMED_AT = COLOR_AT + COLOR_DUR + 320;
const SHARE_AT = NAMED_AT + 850;

export default function RevealCeremony({ model, hologram, slicesSolved, onDismiss }) {
  const [phase, setPhase] = useState(PHASES.intro);
  const [blend, setBlend] = useState(0);
  const [fadeIn, setFadeIn] = useState(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setFadeIn(true));
    let cancelled = false;
    let colorRaf = 0;

    const timers = [
      setTimeout(() => setPhase(PHASES.assemble), INTRO_MS),
      setTimeout(() => setPhase(PHASES.hold), INTRO_MS + ASSEMBLE_MS),
      setTimeout(() => setPhase(PHASES.colorize), COLOR_AT),
      setTimeout(() => setPhase(PHASES.named), NAMED_AT),
      setTimeout(() => setPhase(PHASES.share), SHARE_AT),
    ];

    const colorTimer = setTimeout(() => {
      const start = performance.now();
      const tick = (now) => {
        if (cancelled) return;
        const u = Math.min(1, (now - start) / COLOR_DUR);
        setBlend(u);
        if (u < 1) colorRaf = requestAnimationFrame(tick);
        else setBlend(1);
      };
      colorRaf = requestAnimationFrame(tick);
    }, COLOR_AT);

    return () => {
      cancelled = true;
      cancelAnimationFrame(t);
      cancelAnimationFrame(colorRaf);
      timers.forEach(clearTimeout);
      clearTimeout(colorTimer);
    };
  }, []);

  const showHolo = phase >= PHASES.assemble;
  const showTitle = phase >= PHASES.named;
  const showShare = phase >= PHASES.share;
  const statusLine =
    phase === PHASES.intro
      ? 'Locking reconstruction…'
      : phase === PHASES.assemble
        ? 'Assembling matter…'
        : phase === PHASES.hold
          ? 'Form locked. Priming palette…'
          : phase === PHASES.colorize
            ? 'Colors popping in…'
            : null;

  const burstKick =
    phase === PHASES.colorize && blend > 0 && blend < 1
      ? Math.sin(blend * Math.PI) * 0.055
      : 0;
  const glowWarm =
    phase === PHASES.colorize
      ? 0.2 + Math.sin(Math.min(1, blend) * Math.PI) * 0.55
      : phase === PHASES.hold
        ? 0.35
        : phase === PHASES.assemble
          ? 0.4
          : 0.15;

  return (
    <div className={`atelier-reveal${fadeIn ? ' is-in' : ''}`}>
      <div className="atelier-reveal-inner">
        {showTitle ? (
          <>
            <div className="atelier-reveal-pill">● MYSTERY SOLVED — 100% COMPLETE</div>
            <h1 className="atelier-reveal-title">It was a {model.name}</h1>
            <p className="atelier-reveal-sub">
              {model.category ? `${String(model.category).toUpperCase()} · ARCHIVE` : 'SYNDICATE ARCHIVE'}
            </p>
          </>
        ) : (
          <p className="atelier-reveal-status">{statusLine}</p>
        )}

        <div
          className="atelier-reveal-stage"
          style={{
            opacity: showHolo ? 1 : 0,
            transform: showHolo ? `scale(${1 + burstKick})` : 'scale(0.92)',
            filter:
              phase === PHASES.colorize
                ? `drop-shadow(0 0 ${16 + glowWarm * 28}px rgba(232,119,42,${0.25 + glowWarm * 0.35}))`
                : phase >= PHASES.assemble
                  ? 'drop-shadow(0 12px 28px rgba(0,0,0,0.55))'
                  : 'none',
          }}
        >
          {showTitle && (
            <div className="atelier-reveal-stage-chrome">
              <span className="atelier-chip">360° DIORAMA</span>
              <span className="atelier-chip atelier-chip--soft">RESTORED</span>
            </div>
          )}
          {showHolo && (
            <HologramViewport
              size={hologram.size}
              voxels={hologram.voxels}
              palette={hologram.palette}
              grayscale={false}
              colorBlend={blend}
              colorBurst
              autoRotate={blend > 0.55 || phase >= PHASES.named}
              presentation="trophy"
              initialYaw={FRONT_YAW}
              assemble
              assembleMs={ASSEMBLE_MS}
              height={260}
            />
          )}
          {showTitle && (
            <div className="atelier-reveal-stage-foot">
              <span>{model.name}</span>
              <span>{slicesSolved} slices united</span>
            </div>
          )}
        </div>

        {showShare && (
          <div className="atelier-reveal-share">
            <ShareCard model={model} pct={100} slicesSolved={slicesSolved} />
            <button type="button" className="atelier-btn-primary atelier-reveal-continue" onClick={onDismiss}>
              Continue →
            </button>
            <p className="atelier-reveal-archived">Restoration archived to Collection</p>
          </div>
        )}

        {!showShare && phase >= PHASES.assemble && (
          <p className="atelier-reveal-hint">
            {phase === PHASES.assemble
              ? 'Cubes converging…'
              : phase === PHASES.hold
                ? 'Hold for palette burst…'
                : phase === PHASES.colorize
                  ? 'Pop · pop · pop — full color!'
                  : 'Drag to inspect'}
          </p>
        )}
      </div>
    </div>
  );
}
